import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import Divider from 'LuxKit/Divider'
import TextLink from 'LuxKit/TextLink'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { LOYALTY_SPEND_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getLoyaltyActionRewardsSummary from 'loyalty/lib/getLoyaltyActionRewardsSummary'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierNormalBackgroundStyle } from 'styles/utils/tierStyles'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'

/** “Spend my points” only when total account balance is above this. */
const SPEND_CTA_MIN_LOYALTY_POINTS_BALANCE = 1000

interface Props {
  rewards: Array<App.LoyaltyActionReward>
  tier: App.LoyaltyTier
}

const SummaryCard = styled(Sheet)`
  overflow: hidden;
`

const SummaryGrid = styled.div`
  display: grid;
  gap: ${rem(20)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr auto 1fr;
    align-items: stretch;
    gap: ${rem(28)};
  }
`

const ProgressTrack = styled.div`
  flex: 1;
  position: relative;
  width: 100%;
  height: ${rem(6)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.round};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  display: flex;
  flex-direction: row;
  align-items: center;
`

const ProgressFill = styled.div`
  border-radius: ${props => props.theme.borderRadius.round};
  height: 100%;
  transition: width 0.35s ease;
  ${tierNormalBackgroundStyle}
`

/**
 * Skeleton mirroring the summary card shape while action rewards load.
 */
export function LoyaltyActionRewardsSummaryLoadingSkeleton() {
  return (
    <SummaryCard
      background="normal-background"
      borderRadius="M"
      shadow="flat-small"
      padding={16}
    >
      <SummaryGrid>
        <Group direction="vertical" gap={12}>
          <TextLoadingBox typography="body-small" width="50%" />
          <TextLoadingBox typography="body-medium" width="80%" />
          <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
            <LoadingBox height={6} borderRadius="round" style={{ flex: 1 }} />
            <TextLoadingBox typography="caption-medium" width="3ch" />
          </Group>
        </Group>
        <Divider kind="secondary" direction="horizontal" tabletDirection="vertical" />
        <Group direction="vertical" gap={12}>
          <TextLoadingBox typography="body-small" width="40%" />
          <TextLoadingBox typography="body-medium" width="70%" />
        </Group>
      </SummaryGrid>
    </SummaryCard>
  )
}

function LoyaltyActionRewardsSummary({ rewards, tier }: Props) {
  const intl = useIntl()
  const loyaltyPointsBalance = useAppSelector(state => state.loyalty.account.data?.points)
  const summary = useMemo(() => getLoyaltyActionRewardsSummary(rewards), [rewards])

  const pct = summary.totalCount > 0
    ? Math.round((summary.completedCount / summary.totalCount) * 100)
    : 0

  const showSpendPointsCta = loyaltyPointsBalance != null
    && loyaltyPointsBalance > SPEND_CTA_MIN_LOYALTY_POINTS_BALANCE

  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const statusTitle = intl.formatMessage({
    id: 'UBoRNXHRmPbg6Jt_diwp',
    defaultMessage: 'Your quests completion',
    description: 'Column title for completion progress in the Société Quests summary card',
  })
  const pointsTitle = intl.formatMessage({
    id: 'R70kquVLvx0l0uCGvzyd',
    defaultMessage: '{programName} Points earned',
    description: 'Column title for points earned versus available in the Société Quests summary card',
  }, { programName: loyaltyProgramName })
  const spendCtaLabel = intl.formatMessage({
    id: '7D6UYxGjrDUUAJcis-1F',
    defaultMessage: 'Spend my points',
    description: 'CTA in Société Quests summary that navigates to experiences or benefits redemption',
  })

  const pointsEarnedFormatted = intl.formatNumber(summary.pointsEarned, {
    maximumFractionDigits: 0,
  })
  const pointsAvailableFormatted = intl.formatNumber(summary.pointsAvailable, {
    maximumFractionDigits: 0,
  })
  const percentLabel = intl.formatNumber(pct / 100, {
    style: 'percent',
    maximumFractionDigits: 0,
  })

  const renderBold = useCallback(
    (chunks: ReactNode) => <strong>{chunks}</strong>,
    [],
  )

  return (
    <ProductPaletteProvider palette="default">
      <SummaryCard background="normal-background" borderRadius="M" shadow="flat-small" padding={16}>
        <SummaryGrid>
          <Group direction="vertical" gap={12}>
            <BodyText variant="small" weight="bold" colour="neutral-one" align="start">
              {statusTitle}
            </BodyText>
            <BodyText variant="medium" colour="neutral-two" as="div">
              <FormattedMessage
                id="-spcwYJTuXRJZKCwA2QL"
                defaultMessage="You’ve completed <b>{completed}</b> of <b>{total}</b> quests."
                description="Completion summary body on Société Quests card with bold completed and total counts"
                values={{
                  completed: summary.completedCount,
                  total: summary.totalCount,
                  b: renderBold,
                }}
              />
            </BodyText>
            <Group direction="horizontal" gap={12} verticalAlign="center" fullWidth>
              <ProgressTrack>
                <ProgressFill
                  className={`tier-${tier}`}
                  style={{ width: `${pct}%` }}
                />
              </ProgressTrack>
              <Caption variant="large" weight="bold" colour={`tier-${tier}-dark`} align="end">
                {percentLabel}
              </Caption>
            </Group>
          </Group>
          <Divider kind="secondary" direction="horizontal" tabletDirection="vertical" />
          <Group direction="vertical" gap={12}>
            <BodyText variant="small" weight="bold" colour="neutral-one" align="start">
              {pointsTitle}
            </BodyText>
            <Group direction="vertical" gap={4} horizontalAlign="start">
              <BodyText variant="medium" colour="neutral-two" as="div">
                <FormattedMessage
                  id="9L9oB-RrHFNaFgqUuwui"
                  defaultMessage="You’ve earned <b>{earned}</b> of <b>{max}</b> points by completing quests."
                  description="Points earned versus available summary on Société Quests card with bold numbers"
                  values={{
                    earned: pointsEarnedFormatted,
                    max: pointsAvailableFormatted,
                    b: renderBold,
                  }}
                />
              </BodyText>
              {showSpendPointsCta && (
                <TextLink
                  to={`/${LOYALTY_SPEND_POINTS_PAGE}`}
                  colour="neutral-one"
                  size="medium"
                  weight="bold"
                  underline
                  block
                >
                  {spendCtaLabel}
                </TextLink>
              )}
            </Group>
          </Group>
        </SummaryGrid>
      </SummaryCard>
    </ProductPaletteProvider>
  )
}

export default LoyaltyActionRewardsSummary
